import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CanDeactivatePostAdGuard } from './core/guards/can-deactivate-post-ad-dialog.guard';
import { SeoResolver } from './core/resolvers/seo.resolver';
import { PlatformUpdatesComponent } from './platform-updates/platform-updates.component';
import { AboutUsComponent } from './routes/about-us/about-us.component';
import { AdsAnalyticsComponent } from './routes/ads-analytics/ads-analytics.component';
import { AdComponent } from './routes/ads/ad/ad.component';
import { AdsComponent } from './routes/ads/ads.component';
import { AppsComponent } from './routes/apps/apps.component';
import { BarcodeScannerComponent } from './routes/barcode-scanner/barcode-scanner.component';
import { CampaignComponent } from './routes/campaign/campaign.component';
import { HelpTopicComponent } from './routes/help/help-topic/help-topic.component';
import { HelpComponent } from './routes/help/help.component';
import { HomeComponent } from './routes/home/home.component';
import { LogupComponent } from './routes/logup/logup.component';
import { PageNotFoundComponent } from './routes/page-not-found/page-not-found.component';
import { PaymentTempComponent } from './routes/payment-temp/payment-temp.component';
import { PaymentFailComponent } from './routes/payment/payment-fail/payment-fail.component';
import { PaymentSuccessComponent } from './routes/payment/payment-success/payment-success.component';
import { PaymentComponent } from './routes/payment/payment.component';
import { PlaceAdSuccessComponent } from './routes/place-ad/place-ad-success/place-ad-success.component';
import { PlaceAdComponent } from './routes/place-ad/place-ad.component';
import { PremiumComponent } from './routes/premium/premium.component';
import { PricingComponent } from './routes/pricing/pricing.component';
import { UserAdsComponent } from './routes/user/user-ads/user-ads.component';
import { UserEditAdComponent } from './routes/user/user-edit-ad/user-edit-ad.component';
import { UserProcessComponent } from './routes/user/user-process/user-process.component';
import { UserProfileChangePhoneComponent } from './routes/user/user-profile/user-profile-change-phone/user-profile-change-phone.component';
import { UserProfileComponent } from './routes/user/user-profile/user-profile.component';
import { UserResetPasswordComponent } from './routes/user/user-reset-password/user-reset-password.component';
import { UserVerifyComponent } from './routes/user/user-verify/user-verify.component';
import { VipComponent } from './routes/vip/vip.component';
import { AuthGuard } from './core/guards/auth.guard';
import { ConfirmationOtpComponent } from './shared/confirmation-otp/confirmation-otp.component';
import { ExitGuard } from './core/guards/exit.guard';
import { LanguageRedirectGuard } from './core/guards/language.guard';
import { VerificationGuard } from './core/guards/verification.guard';
import { PaymentProviderComponent } from './routes/payment/payment-provider/payment-provider.component';
import { PaymentCodeComponent } from './routes/payment/payment-code/payment-code.component';
import { MissedCallsComponent } from './routes/missed-calls/missed-calls.component';
import { PaymentTierComponent } from './routes/payment/payment-tier/payment-tier.component';
import { PaymentConfirmComponent } from './routes/payment/payment-confirm/payment-confirm.component';
import { VipPlusComponent } from './routes/vip-plus/vip-plus.component';
import { CompanyPagesComponent } from './routes/company-pages/company-pages.component';
import { CustomerFeedbackComponent } from './routes/customer-feedback/customer-feedback.component';
import { UserPreferencesComponent } from './routes/user/user-preferences/user-preferences.component';
import { detailRouteMatcher, localizedDetailRouteMatcher } from './core/routing/detail-route.matcher';

const routes: Routes = [
	{
		path: '',
		component: HomeComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/',
		component: HomeComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'about',
		component: AboutUsComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/about',
		component: AboutUsComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'platform-updates',
		component: PlatformUpdatesComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'ads-analytics',
		component: AdsAnalyticsComponent,
		canActivate: [LanguageRedirectGuard, AuthGuard, VerificationGuard],
	},
	{
		path: ':lang/ads-analytics',
		component: AdsAnalyticsComponent,
		canActivate: [AuthGuard, VerificationGuard],
	},
	{
		path: 'user/:userID/confirm-otp',
		component: ConfirmationOtpComponent,
		canActivate: [LanguageRedirectGuard, AuthGuard],
	},
	{
		path: ':lang/user/:userID/confirm-otp',
		component: ConfirmationOtpComponent,
		canActivate: [AuthGuard],
	},
	{
		path: 'login',
		component: LogupComponent,
		canActivate: [LanguageRedirectGuard, AuthGuard, VerificationGuard],
		canDeactivate: [ExitGuard],
	},
	{
		path: ':lang/login',
		component: LogupComponent,
		canActivate: [AuthGuard, VerificationGuard],
		canDeactivate: [ExitGuard],
	},
	{
		path: 'placead',
		component: PlaceAdComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
		canDeactivate: [ExitGuard],
	},
	{
		path: ':lang/placead',
		component: PlaceAdComponent,
		canActivate: [VerificationGuard],
		canDeactivate: [CanDeactivatePostAdGuard],
	},
	{
		path: 'placead/success',
		component: PlaceAdSuccessComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/placead/success',
		component: PlaceAdSuccessComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'user/:userID/profile',
		canActivate: [LanguageRedirectGuard, AuthGuard, VerificationGuard],
		component: UserProfileComponent,
	},
	{
		path: ':lang/user/:userID/profile',
		canActivate: [AuthGuard, VerificationGuard],
		component: UserProfileComponent,
	},
	{
		path: 'user/:userID/profile/phone',
		canActivate: [LanguageRedirectGuard, AuthGuard, VerificationGuard],
		component: UserProfileChangePhoneComponent,
	},
	{
		path: ':lang/user/:userID/profile/phone',
		canActivate: [AuthGuard, VerificationGuard],
		component: UserProfileChangePhoneComponent,
	},
	{
		path: 'user/:userID/process',
		canActivate: [LanguageRedirectGuard, AuthGuard],
		component: UserProcessComponent,
	},
	{
		path: ':lang/user/:userID/process',
		canActivate: [AuthGuard],
		component: UserProcessComponent,
	},
	{
		path: 'user/:userID/verify',
		canActivate: [LanguageRedirectGuard, AuthGuard],
		component: UserVerifyComponent,
	},
	{
		path: 'user/wa-alerts',
		canActivate: [LanguageRedirectGuard, AuthGuard],
		component: UserPreferencesComponent,
	},
	{
		path: ':lang/user/wa-alerts',
		canActivate: [AuthGuard],
		component: UserPreferencesComponent,
	},
	{
		path: ':lang/user/:userID/verify',
		canActivate: [AuthGuard],
		component: UserVerifyComponent,
	},
	{
		path: 'user/:userID/ads',
		component: UserAdsComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/user/:userID/ads',
		component: UserAdsComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'user/resetpassword',
		component: UserResetPasswordComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/user/resetpassword',
		component: UserResetPasswordComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'c/:category',
		component: AdsComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
		resolve: [SeoResolver],
	},
	{
		path: ':lang/c/:category',
		component: AdsComponent,
		canActivate: [VerificationGuard],
		resolve: [SeoResolver],
	},

	{
		path: 'ad/:id/edit',
		canActivate: [LanguageRedirectGuard, AuthGuard, VerificationGuard],
		canDeactivate: [ExitGuard],
		component: UserEditAdComponent,
	},
	{
		path: ':lang/ad/:id/edit',
		canActivate: [AuthGuard, VerificationGuard],
		canDeactivate: [ExitGuard],
		component: UserEditAdComponent,
	},
	{
		path: 'ad/:id',
		component: AdComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/ad/:id',
		component: AdComponent,
		canActivate: [VerificationGuard],
	},
	{
		matcher: detailRouteMatcher,
		component: AdComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		matcher: localizedDetailRouteMatcher,
		component: AdComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'ad/view/:id',
		component: AdComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/ad/view/:id',
		component: AdComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'index.php/ad/view/:id',
		component: AdComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/index.php/ad/view/:id',
		component: AdComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'premium',
		component: PremiumComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/premium',
		component: PremiumComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'vip',
		component: VipComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/vip',
		component: VipComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'hot',
		loadChildren: () => import('./routes/vip-ad-list/vip-ad-list.module').then((m) => m.VipAdListModule),
		canActivate: [LanguageRedirectGuard],
	},
	{
		path: ':lang/hot',
		loadChildren: () => import('./routes/vip-ad-list/vip-ad-list.module').then((m) => m.VipAdListModule),
	},
	{
		path: 'pricing',
		component: PricingComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/pricing',
		component: PricingComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'payment',
		component: PaymentComponent,
		canActivate: [LanguageRedirectGuard, AuthGuard, VerificationGuard],
	},
	{
		path: ':lang/payment',
		component: PaymentComponent,
		canActivate: [AuthGuard, VerificationGuard],
	},
	{
		path: 'payment/temp',
		component: PaymentTempComponent,
		canActivate: [LanguageRedirectGuard],
	},
	{
		path: ':lang/payment/temp',
		component: PaymentTempComponent,
	},
	{
		path: 'payment/success',
		component: PaymentSuccessComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/payment/success',
		component: PaymentSuccessComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'payment/fail',
		component: PaymentFailComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/payment/fail',
		component: PaymentFailComponent,
		canActivate: [VerificationGuard],
	},

	{
		path: 'payment/provider',
		component: PaymentProviderComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},

	{
		path: ':lang/payment/provider',
		component: PaymentProviderComponent,
		canActivate: [VerificationGuard],
	},

	{
		path: 'payment/provider/code',
		component: PaymentCodeComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},

	{
		path: ':lang/payment/provider/code',
		component: PaymentCodeComponent,
		canActivate: [VerificationGuard],
	},

	{
		path: 'payment/tier',
		component: PaymentTierComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},

	{
		path: ':lang/payment/tier',
		component: PaymentTierComponent,
		canActivate: [VerificationGuard],
	},

	{
		path: 'payment/confirm',
		component: PaymentConfirmComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},

	{
		path: ':lang/payment/confirm',
		component: PaymentConfirmComponent,
		canActivate: [VerificationGuard],
	},

	{
		path: 'missed-calls',
		component: MissedCallsComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},

	{
		path: ':lang/missed-calls',
		component: MissedCallsComponent,
		canActivate: [VerificationGuard],
	},

	{
		path: 'help',
		component: HelpComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/help',
		component: HelpComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'help/:topic',
		component: HelpTopicComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/help/:topic',
		component: HelpTopicComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'apps',
		component: AppsComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'campaign',
		component: CampaignComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/campaign',
		component: CampaignComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'privacy-policy',
		component: CompanyPagesComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
		data: {
			title: 'Privacy Policy',
			content1: `Our "Privacy Policy" explains how we as a company, DCH Software Limited - TA Kurd Shopping, collects, uses, and shares users' information when they utilize our services, such as visiting our websites, using the mobile application Kurd Shopping, and engaging in related activities`,
			content2: `'Users' privacy rights and options are described, and contacting us at`,
			content3: ` is advised for questions or concerns`,
			readText: 'Read our full Privacy Policy:',
			mailTo: 'hello@kurdshopping.com',
			buttonText: 'Privacy Policy',
			pdfLink: 'assets/policies-pdf/privacy-policy.pdf',
		},
	},
	{
		path: ':lang/privacy-policy',
		component: CompanyPagesComponent,
		canActivate: [VerificationGuard],
		data: {
			title: 'Privacy Policy',
			content1: `Our "Privacy Policy" explains how we as a company, DCH Software Limited - TA Kurd Shopping, collects, uses, and shares users' information when they utilize our services, such as visiting our websites, using the mobile application Kurd Shopping, and engaging in related activities`,
			content2: `'Users' privacy rights and options are described, and contacting us at `,
			content3: ` is advised for questions or concerns`,
			readText: 'Read our full Privacy Policy:',
			mailTo: 'hello@kurdshopping.com',
			buttonText: 'Privacy Policy',
			pdfLink: 'assets/policies-pdf/privacy-policy.pdf',
		},
	},
	{
		path: 'cookies-policy',
		component: CompanyPagesComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
		data: {
			title: 'Cookie Policy',
			content1:
				'We use first- and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our Website for advertising, analytics, and other purposes.',
			readText: 'Read our full Cookie Policy:',
			buttonText: 'Cookie Policy',
			pdfLink: 'assets/policies-pdf/cookies-policy.pdf',
		},
	},

	{
		path: ':lang/cookies-policy',
		component: CompanyPagesComponent,
		canActivate: [VerificationGuard],
		data: {
			title: 'Cookie Policy',
			content1:
				'We use first- and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our Website for advertising, analytics, and other purposes.',
			readText: 'Read our full Cookie Policy:',
			buttonText: 'Cookie Policy',
			pdfLink: 'assets/policies-pdf/cookies-policy.pdf',
		},
	},
	{
		path: 'terms',
		component: CompanyPagesComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
		data: {
			title: 'Terms of Service',
			content1:
				'The "Terms of Service" outline the legal agreement between users and DCH Software Limited, operating as Kurd Shopping. This agreement covers the use of our services, including the website ',
			content2: ' mobile app, and related products.',
			content3:
				'Kurd Shopping serves as a classified buying and selling platform for users to list items for sale. Users can contact Kurd Shopping through various means, including phone and email.',
			readText: 'Read our full Terms of Service:',
			websiteLink: 'https://kurdshopping.com',
			buttonText: 'Terms of Service',
			pdfLink: 'assets/policies-pdf/terms.pdf',
		},
	},
	{
		path: ':lang/terms',
		component: CompanyPagesComponent,
		canActivate: [VerificationGuard],
		data: {
			title: 'Terms of Service',
			content1:
				'The "Terms of Service" outline the legal agreement between users and DCH Software Limited, operating as Kurd Shopping. This agreement covers the use of our services, including the website ',
			content2: ' mobile app, and related products.',
			content3:
				'Kurd Shopping serves as a classified buying and selling platform for users to list items for sale. Users can contact Kurd Shopping through various means, including phone and email.',
			readText: 'Read our full Terms of Service:',
			websiteLink: 'https://kurdshopping.com',
			buttonText: 'Terms of Service',
			pdfLink: 'assets/policies-pdf/terms.pdf',
		},
	},
	// {
	// 	path: 'barcode-scanner',
	// 	component: BarcodeScannerComponent,
	// 	canActivate: [LanguageRedirectGuard, VerificationGuard],
	// },
	// {
	// 	path: ':lang/barcode-scanner',
	// 	component: BarcodeScannerComponent,
	// 	canActivate: [VerificationGuard],
	// },
	{
		path: 'vip-plus',
		component: VipPlusComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/vip-plus',
		component: VipPlusComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: 'customer-feedback',
		component: CustomerFeedbackComponent,
		canActivate: [LanguageRedirectGuard, VerificationGuard],
	},
	{
		path: ':lang/customer-feedback',
		component: CustomerFeedbackComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: ':lang',
		component: HomeComponent,
		canActivate: [VerificationGuard],
	},
	{
		path: '**',
		component: PageNotFoundComponent,
		canActivate: [VerificationGuard],
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			scrollPositionRestoration: 'enabled',
			onSameUrlNavigation: 'reload',
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
